/**
 * Verantwortlichkeitsbereiche — the management surface (RESP2c).
 *
 * An orchestrator and nothing else: it owns which area is selected, which
 * dialog is open, and who is allowed to act. Everything that renders
 * lives in `./responsibilities/` (Regel 48), which is why this file is
 * short enough to read in one go despite the page doing considerably
 * more than it did in RESP1.
 *
 * ── What changed, and why it is now honest ────────────────────────────
 *
 * RESP1 refused an add button, a Typ column and an organisations KPI,
 * because at the time the 13 category codes were CHECK-constrained and
 * there was no team model. Two of those three constraints are gone:
 * RESP2b gave every area a row with its own id, label, icon and colour,
 * and made custom areas creatable and deletable. So the button is real
 * now — it posts to an endpoint that exists.
 *
 * The team model still does not exist, and this page still does not
 * invent one. "Team" on a badge is a description of a configuration that
 * names several people, never an entity and never an id — see
 * `assignmentKind`.
 *
 * ── Where the numbers come from ───────────────────────────────────────
 *
 * The KPIs are computed from the areas already loaded, so they cannot
 * disagree with the rows beneath them. The table's operational figures
 * ride along in the list response. Only the all-time totals — the ones
 * that decide whether a delete is possible — need their own request, and
 * that request is made once per SELECTION, never once per row.
 *
 * ── Who may do what ──────────────────────────────────────────────────
 *
 * The server lets a manager READ this configuration (`require_roles
 * ("admin", "manager")` on the reads) and only an admin write it. That
 * split is mirrored exactly: a manager sees who is responsible for what,
 * which is genuinely useful, and is offered no control that could only
 * ever answer 403.
 */
import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../../contexts/AuthContext";
import { patchResponsibilityArea } from "../../api/adminData";

import ResponsibilityAreaDeleteDialog from "./responsibilities/ResponsibilityAreaDeleteDialog";
import ResponsibilityAreaDetail from "./responsibilities/ResponsibilityAreaDetail";
import ResponsibilityAreaFormDialog from "./responsibilities/ResponsibilityAreaFormDialog";
import ResponsibilityAreaKpis from "./responsibilities/ResponsibilityAreaKpis";
import ResponsibilityAreaList from "./responsibilities/ResponsibilityAreaList";
import { invalidateActivityCategoryCatalog } from "./activityCategories/activityCategoryCatalogStore";
import useAreaUsage from "./responsibilities/useAreaUsage";
import useResponsibilityAreas from "./responsibilities/useResponsibilityAreas";

// Re-exported rather than redefined: the wording belongs to the panel
// that shows it, and one definition cannot drift from itself.
export { warningLabel } from "./responsibilities/ResponsibilityAreaDetail";

const PANEL =
  "rounded-[14px] border border-border bg-card shadow-[0_1px_2px_rgba(16,24,40,0.04)] dark:border-[#1e2130] dark:bg-[#13161f]";

export default function AdminResponsibilityAreasPage() {
  const { areas, loading, error, refresh } = useResponsibilityAreas();
  const { user } = useAuth();
  const canEdit = user?.role === "admin";

  const [selectedId, setSelectedId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formArea, setFormArea] = useState(null);
  const [deleteArea, setDeleteArea] = useState(null);

  /**
   * The selection, resolved against what is actually loaded.
   *
   * Derived rather than stored so a refresh, a rename or a delete cannot
   * leave the panel pointing at something that is no longer there. The
   * opening choice is the first ACTIVE area — an inactive one is the
   * least likely thing a person came here to look at, and landing on it
   * would make the page look misconfigured on arrival.
   */
  const selected = useMemo(() => {
    const list = Array.isArray(areas) ? areas : [];
    return (
      list.find((a) => a.id === selectedId) ||
      list.find((a) => a.is_active) ||
      list[0] ||
      null
    );
  }, [areas, selectedId]);

  const {
    usage,
    loading: usageLoading,
    error: usageError,
    refresh: refreshUsage,
  } = useAreaUsage(selected?.id);

  const openCreate = useCallback(() => {
    setFormArea(null);
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((area) => {
    setFormArea(area);
    setFormOpen(true);
  }, []);

  /**
   * Deleting also SELECTS, and that is not a convenience.
   *
   * The dialog needs this area's all-time totals to know whether a
   * delete is even possible, and those are loaded for the selection.
   * Selecting first means the dialog reads real numbers for the area it
   * names, instead of borrowing whichever area's numbers happened to be
   * on screen.
   */
  const openDelete = useCallback((area) => {
    setSelectedId(area.id);
    setDeleteArea(area);
  }, []);

  /** Selects what was just saved, so the panel shows the result. */
  const handleSaved = useCallback(
    (saved) => {
      if (saved?.id) setSelectedId(saved.id);
      void refresh();
      void refreshUsage();
      // RESP2d: a create or a rename changes what a task form may offer
      // and what a task list must call an area. Without this the two
      // surfaces disagree until someone reloads the page — and the one
      // showing the old name looks correct, which is the worst kind of
      // wrong.
      void invalidateActivityCategoryCatalog();
    },
    [refresh, refreshUsage]
  );

  /**
   * The one-click state change on the row menu.
   *
   * A PATCH with a single key, because absent means untouched: nothing
   * else about the area is rewritten by flipping its availability.
   */
  const toggleActive = useCallback(
    async (area) => {
      try {
        await patchResponsibilityArea(area.id, { is_active: !area.is_active });
        toast.success(
          area.is_active
            ? `„${area.label}" wurde deaktiviert.`
            : `„${area.label}" wurde aktiviert.`
        );
        await refresh();
        // RESP2d: deactivating removes it from every form's options;
        // activating puts it back.
        void invalidateActivityCategoryCatalog();
      } catch (err) {
        toast.error(
          err?.message || "Der Status konnte nicht geändert werden."
        );
      }
    },
    [refresh]
  );

  const handleDeleted = useCallback(() => {
    // Cleared, not repointed: the derived selection above picks the next
    // sensible area on its own once the list comes back without this one.
    setSelectedId(null);
    setDeleteArea(null);
    void refresh();
    // RESP2d: a deleted area must stop being offered immediately.
    void invalidateActivityCategoryCatalog();
  }, [refresh]);

  const total = Array.isArray(areas) ? areas.length : 0;

  return (
    <div className="mx-auto max-w-[1400px] p-4 md:p-6">
      <nav
        aria-label="Breadcrumb"
        className="mb-4 text-[11px] text-muted-foreground dark:text-[#4a5070]"
      >
        <Link
          to="/admin"
          className="rounded-[4px] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Verwaltung
        </Link>
        <span className="mx-2" aria-hidden="true">
          /
        </span>
        <span aria-current="page" className="text-foreground dark:text-[#94a3b8]">
          Verantwortlichkeitsbereiche
        </span>
      </nav>

      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="m-0 text-[24px] font-bold tracking-tight text-foreground">
            Verantwortlichkeitsbereiche
          </h1>
          <p className="mt-1.5 max-w-[760px] text-[13px] leading-relaxed text-muted-foreground">
            Legen Sie fest, in welchen Bereichen Ihre Organisation arbeitet und
            wer bei neuen Aufgaben standardmässig vorgeschlagen wird. Die
            Vorschläge lassen sich beim Erfassen jederzeit ändern — bestehende
            Aufgaben werden nie verändert.
          </p>
        </div>
        {canEdit ? (
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex shrink-0 items-center gap-2 rounded-[10px] bg-violet-600 px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Plus aria-hidden="true" className="h-4 w-4" strokeWidth={2.25} />
            Bereich hinzufügen
          </button>
        ) : null}
      </header>

      <div className="mb-5">
        <ResponsibilityAreaKpis areas={areas} loading={loading} />
      </div>

      {error ? (
        <p role="alert" className="m-0 mb-4 text-[13px] text-destructive">
          {error}
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
        <section aria-label="Tätigkeitsbereiche" className={PANEL}>
          <div className="overflow-x-auto">
            <ResponsibilityAreaList
              areas={areas}
              loading={loading}
              selectedId={selected?.id || null}
              onSelect={setSelectedId}
              canEdit={canEdit}
              onEdit={openEdit}
              onToggleActive={toggleActive}
              onDelete={openDelete}
              onAdd={openCreate}
            />
          </div>
          {total > 0 ? (
            <div className="border-t border-border px-4 py-3">
              <p className="m-0 text-[12px] text-muted-foreground">
                {/* Every area is on one page — there is no pagination to
                    lie about, so the sentence states the whole range.
                    Built as ONE expression: the CI build wraps JSX children,
                    so "literal + expression" text arrives split there and
                    whole locally — a divergence that only shows up after a
                    push. */}
                {`Zeige 1 bis ${total} von ${total} Einträgen`}
              </p>
            </div>
          ) : null}
        </section>

        <aside
          aria-label="Bereich Details"
          className={PANEL + " h-fit xl:sticky xl:top-6"}
        >
          <ResponsibilityAreaDetail
            area={selected}
            usage={usage}
            usageLoading={usageLoading}
            usageError={usageError}
            canEdit={canEdit}
            onEdit={openEdit}
          />
          {!canEdit && selected ? (
            <p className="m-0 border-t border-border px-5 py-3 text-[12px] text-muted-foreground">
              Änderungen an dieser Konfiguration sind Administratoren
              vorbehalten.
            </p>
          ) : null}
        </aside>
      </div>

      <ResponsibilityAreaFormDialog
        open={formOpen}
        area={formArea}
        onOpenChange={setFormOpen}
        onSaved={handleSaved}
      />

      <ResponsibilityAreaDeleteDialog
        open={Boolean(deleteArea)}
        area={deleteArea}
        // `openDelete` selected this area first, so these figures are
        // its own. The id check is the guard that keeps it that way if
        // that ever stops being true.
        usage={deleteArea && deleteArea.id === selected?.id ? usage : null}
        usageLoading={
          deleteArea ? deleteArea.id !== selected?.id || usageLoading : false
        }
        onOpenChange={(open) => {
          if (!open) setDeleteArea(null);
        }}
        onDeleted={handleDeleted}
        onDeactivate={toggleActive}
      />
    </div>
  );
}
